
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { UserRole } from "@/lib/types";
import { SUPABASE_URL, supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const formSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  name: z.string().optional(),
  companyName: z.string().optional(),
  roles: z.array(z.string()).min(1, "Select at least one role"),
});

type FormValues = z.infer<typeof formSchema>;

interface InviteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInviteSuccess: () => void;
}

export function InviteUserDialog({
  open,
  onOpenChange,
  onInviteSuccess,
}: InviteUserDialogProps) {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      name: "",
      companyName: "",
      roles: ["USER"],
    },
  });

  const onSubmit = async (data: FormValues) => {
    if (!user) {
      toast.error("You must be logged in to invite users");
      return;
    }

    setIsSubmitting(true);
    try {
      // Generate a secure token
      const token = uuidv4();
      
      // Calculate expiration date (7 days from now)
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);
      
      // Save the invitation to the database
      const { error: dbError } = await supabase
        .from("user_invitations")
        .insert({
          email: data.email,
          name: data.name || null,
          company_name: data.companyName || null,
          roles: data.roles,
          token: token,
          invited_by: user.id,
          expires_at: expiresAt.toISOString(),
        });

      if (dbError) {
        if (dbError.code === "23505") {
          // Unique violation - email already invited
          toast.error("This email has already been invited and is pending");
        } else {
          toast.error(`Error creating invitation: ${dbError.message}`);
        }
        console.error("Error creating invitation:", dbError);
        return;
      }

      // Send invitation email
      const { error: emailError } = await supabase.functions.invoke(
        "send-invitation-email",
        {
          body: {
            email: data.email,
            name: data.name,
            companyName: data.companyName,
            roles: data.roles,
            token: token,
            invitedBy: {
              name: user.name,
              email: user.email,
            },
          },
        }
      );

      if (emailError) {
        toast.error(`Error sending invitation email: ${emailError.message}`);
        console.error("Error sending invitation email:", emailError);
        return;
      }

      toast.success(`Invitation sent to ${data.email}`);
      form.reset();
      onInviteSuccess();
      onOpenChange(false);
    } catch (error: any) {
      toast.error(`Error: ${error.message || "Something went wrong"}`);
      console.error("Error in invite submission:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const availableRoles: { label: string; value: UserRole }[] = [
    { label: "User", value: "USER" },
    { label: "Admin", value: "ADMIN" },
    { label: "Developer", value: "DEVELOPER" },
    { label: "Tester", value: "TESTER" },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Invite new user</DialogTitle>
          <DialogDescription>
            Send an invitation email to add a new user to the system.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="user@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name (optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="companyName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company (optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="Company Inc." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="roles"
              render={() => (
                <FormItem>
                  <div>
                    <FormLabel>Roles</FormLabel>
                    <FormDescription>
                      Select the roles this user should have.
                    </FormDescription>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {availableRoles.map((role) => (
                      <FormItem
                        key={role.value}
                        className="flex flex-row items-center space-x-3 space-y-0"
                      >
                        <FormControl>
                          <Checkbox
                            checked={form.watch("roles").includes(role.value)}
                            onCheckedChange={(checked) => {
                              const currentRoles = form.watch("roles");
                              if (checked) {
                                form.setValue("roles", [
                                  ...currentRoles,
                                  role.value,
                                ]);
                              } else {
                                form.setValue(
                                  "roles",
                                  currentRoles.filter((r) => r !== role.value)
                                );
                              }
                            }}
                          />
                        </FormControl>
                        <FormLabel className="text-sm font-normal">
                          {role.label}
                        </FormLabel>
                      </FormItem>
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Sending Invitation..." : "Send Invitation"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
